import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../../api/axios';
import {
  Box, Paper, Typography, Chip, Button, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar,
  Divider, IconButton, Tooltip,
} from '@mui/material';
import {
  ArrowBack as BackIcon, Payment as PaymentIcon,
  Delete as DeleteIcon, History as AuditIcon,
  MoneyOff as RefundIcon,
} from '@mui/icons-material';
import useData from '../../../Hooks/useData';
import ApplyPaymentModal from './ApplyPaymentModal';
import IssueRefundModal from './IssueRefundModal';

const STATUS_COLORS = {
  'New': 'default',
  'Partial Payment': 'warning',
  'Payment Received': 'success',
  'Partially Refunded': 'warning',
  'Refunded': 'info',
  'Cancelled': 'error',
};

const OrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { auth } = useData();
  const token = auth?.token;

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [paymentModal, setPaymentModal] = useState(false);
  const [refundModal, setRefundModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, payment: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [refundBanner, setRefundBanner] = useState(null);

  const fetchOrderDetail = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await axios.get(`/api/orders/${orderId}`);
      setData(result.data);
      // Restore banner from latest refund in the data
      if (result.data?.latestRefund) {
        setRefundBanner(result.data.latestRefund);
      }
    } catch (err) {
      setError('Failed to load order details.');
    } finally {
      setIsLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrderDetail();
  }, [fetchOrderDetail]);

  const handleDeletePayment = async () => {
    const payment = deleteConfirm.payment;
    if (!payment) return;
    setIsDeleting(true);
    try {
      await axios.delete(`/api/orders/${orderId}/payments/${payment.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setDeleteConfirm({ open: false, payment: null });
      setSnackbar({ open: true, message: 'Payment removed successfully', severity: 'success' });
      fetchOrderDetail();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to remove payment', severity: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePaymentModalClose = (success) => {
    setPaymentModal(false);
    if (success) {
      setSnackbar({ open: true, message: 'Payment applied successfully', severity: 'success' });
      fetchOrderDetail();
    }
  };

  const handleRefundModalClose = (success, result) => {
    setRefundModal(false);
    if (success && result) {
      setRefundBanner({
        refundedAt: result.refundedAt,
        amount: result.totalAmount,
        cardLastFour: result.cardLastFour,
        cardType: result.cardType,
        method: result.method,
        enteredBy: result.enteredBy,
      });
      setSnackbar({ open: true, message: 'Refund issued successfully', severity: 'success' });
      fetchOrderDetail();
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: "UTC"
    });
  };

  const formatCurrency = (value) => {
    const num = Number(value) || 0;
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (isLoading) {
    return (
      <main>
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">Order Details</h1>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading order details...</Typography>
          </Box>
        </div>
      </main>
    );
  }

  if (error || !data) {
    return (
      <main>
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">Order Details</h1>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          <Box sx={{ p: 3 }}>
            <Alert severity="error">{error || 'Order not found'}</Alert>
            <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/finances')} sx={{ mt: 2 }}>
              Back to Orders
            </Button>
          </Box>
        </div>
      </main>
    );
  }

  const { order, customer, orderItems, paymentHistory, auditLog, summary, refundedItemIds } = data;
  const status = order.Status || 'New';
  const hasRefunds = (summary.totalRefunded || 0) > 0;

  // Build refund banner text
  const buildRefundBannerText = () => {
    if (!refundBanner) return null;
    const when = refundBanner.refundedAt
      ? new Date(refundBanner.refundedAt).toLocaleDateString('en-US', {
          month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
        })
      : '';
    const amount = formatCurrency(refundBanner.amount);
    const by = refundBanner.enteredBy || '';

    if (refundBanner.method === 'card' && refundBanner.cardLastFour) {
      return `Refund issued${when ? ` on ${when}` : ''} — ${amount} refunded to ${refundBanner.cardType || 'Card'} ****${refundBanner.cardLastFour}${by ? ` by ${by}` : ''}. Funds typically appear within 3-5 business days.`;
    }
    return `Refund of ${amount} marked as issued${when ? ` on ${when}` : ''}${by ? ` by ${by}` : ''}.`;
  };

  const refundBannerText = buildRefundBannerText();

  return (
    <main>
      <div className="full-row light-blue">
        <div className="container-fluid">
          <div className="row text-white pt-3 pb-3">
            <div className="col-md-12 text-white">
              <h1 className="text-uppercase">Order #{order.OrderID}</h1>
              <p>Order details and payment management</p>
            </div>
          </div>
        </div>
      </div>

      <div className="full-row white">
        <Box sx={{ p: 3 }}>

          {/* Top action bar */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1, backgroundColor: '#f8f9fa', padding: '16px', borderRadius: '8px' }}>
            <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/finances')} variant="outlined">
              Back to Orders
            </Button>
            <Box sx={{ display: 'flex', gap: 1, '@media (max-width:400px)': { width: '100%' } }}>
              <Button
                startIcon={<RefundIcon />}
                onClick={() => setRefundModal(true)}
                variant="contained"
                sx={{
                  bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' },
                  textTransform: 'uppercase', fontWeight: 600, whiteSpace: 'nowrap',
                  '@media (max-width:400px)': {
                    fontSize: '0.9rem', px: 1, flex: 1,
                    '& .MuiButton-startIcon': { mr: 0.5 },
                  },
                }}
              >
                Issue Refund
              </Button>
              <Button
                startIcon={<PaymentIcon />}
                onClick={() => setPaymentModal(true)}
                variant="contained"
                sx={{
                  bgcolor: '#003063', '&:hover': { bgcolor: '#002145' },
                  textTransform: 'uppercase', fontWeight: 600, whiteSpace: 'nowrap',
                  '@media (max-width:400px)': {
                    fontSize: '0.9rem', px: 1, flex: 1,
                    '& .MuiButton-startIcon': { mr: 0.5 },
                  },
                }}
              >
                Apply Payment
              </Button>
            </Box>
          </Box>

          {/* Refund Banner */}
          {refundBannerText && (
            <Alert
              severity="warning"
              icon={false}
              sx={{ mb: 3, bgcolor: '#fffbeb', border: '1px solid #fcd34d', color: '#78350f', fontWeight: 500 }}
              onClose={() => setRefundBanner(null)}
            >
              {refundBannerText}
            </Alert>
          )}

          {/* Order Summary */}
          <Paper sx={{ p: 3, mb: 3, border: '1px solid #e0e0e0', borderRadius: 2, boxShadow: 'none' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#003063' }}>
              Order Summary
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Customer</Typography>
                <Typography variant="body1" fontWeight="medium">{customer?.name || 'N/A'}</Typography>
                {customer?.email && <Typography variant="body2" color="text.secondary">{customer.email}</Typography>}
                {customer?.churchName && <Typography variant="body2" color="text.secondary">{customer.churchName}</Typography>}
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Chip
                  label={status}
                  color={STATUS_COLORS[status] || 'default'}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Order Date</Typography>
                <Typography variant="body1">{formatDate(order.OrderDate)}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Event</Typography>
                <Typography variant="body1">{order.EventName || order.Products || '-'}</Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Financial Summary Cards */}
            {hasRefunds ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f8f9fa' }} elevation={0}>
                  <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                  <Typography variant="h6" fontWeight="bold">{formatCurrency(summary.grandTotal)}</Typography>
                </Paper>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9' }} elevation={0}>
                  <Typography variant="caption" color="text.secondary">Total Paid</Typography>
                  <Typography variant="h6" fontWeight="bold" color="success.main">
                    {formatCurrency(summary.totalPaid)}
                  </Typography>
                </Paper>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fce4ec' }} elevation={0}>
                  <Typography variant="caption" color="text.secondary">Total Refunded</Typography>
                  <Typography variant="h6" fontWeight="bold" color="error.main">
                    {formatCurrency(summary.totalRefunded)}
                  </Typography>
                </Paper>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fff8e1' }} elevation={0}>
                  <Typography variant="caption" color="text.secondary">Net Received</Typography>
                  <Typography variant="h6" fontWeight="bold" color="warning.dark">
                    {formatCurrency(summary.netReceived)}
                  </Typography>
                </Paper>
              </Box>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f8f9fa' }} elevation={0}>
                  <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                  <Typography variant="h6" fontWeight="bold">{formatCurrency(summary.grandTotal)}</Typography>
                </Paper>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9' }} elevation={0}>
                  <Typography variant="caption" color="text.secondary">Total Paid</Typography>
                  <Typography variant="h6" fontWeight="bold" color="success.main">
                    {formatCurrency(summary.totalPaid)}
                  </Typography>
                </Paper>
                <Paper
                  sx={{ p: 2, textAlign: 'center', bgcolor: summary.remainingBalance > 0 ? '#fbe9e7' : '#e8f5e9' }}
                  elevation={0}
                >
                  <Typography variant="caption" color="text.secondary">Remaining Balance</Typography>
                  <Typography
                    variant="h6" fontWeight="bold"
                    color={summary.remainingBalance > 0 ? 'error.main' : 'success.main'}
                  >
                    {formatCurrency(summary.remainingBalance)}
                  </Typography>
                </Paper>
              </Box>
            )}
          </Paper>

          {/* Order Items */}
          {orderItems && orderItems.length > 0 && (
            <Paper sx={{ p: 3, mb: 3, border: '1px solid #e0e0e0', borderRadius: 2, boxShadow: 'none' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#003063' }}>
                Order Items
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Description</strong></TableCell>
                      <TableCell align="right"><strong>Qty</strong></TableCell>
                      <TableCell align="right"><strong>Unit Price</strong></TableCell>
                      <TableCell align="right"><strong>Total</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orderItems.map((item) => {
                      const isRefunded = (refundedItemIds || []).includes(item.OrderItemID);
                      return (
                        <TableRow key={item.OrderItemID} sx={isRefunded ? { bgcolor: '#fafafa' } : {}}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography
                                variant="body2"
                                sx={isRefunded ? { textDecoration: 'line-through', color: '#9ca3af' } : {}}
                              >
                                {item.Description || '-'}
                              </Typography>
                              {isRefunded && (
                                <Chip label="Refunded" size="small" color="error" variant="outlined"
                                  sx={{ fontSize: '0.7rem', height: 20 }} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={isRefunded ? { color: '#9ca3af' } : {}}>
                            {item.Quantity}
                          </TableCell>
                          <TableCell align="right" sx={isRefunded ? { color: '#9ca3af' } : {}}>
                            {formatCurrency(item.UnitPrice)}
                          </TableCell>
                          <TableCell align="right"
                            sx={{ fontWeight: 'bold', color: isRefunded ? '#ef4444' : 'inherit' }}
                          >
                            {isRefunded ? `-${formatCurrency(item.TotalPrice)}` : formatCurrency(item.TotalPrice)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Payment History */}
          <Paper sx={{ p: 3, mb: 3, border: '1px solid #e0e0e0', borderRadius: 2, boxShadow: 'none' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#003063' }}>
              Payment History
            </Typography>
            {paymentHistory && paymentHistory.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Method</strong></TableCell>
                      <TableCell align="right"><strong>Amount</strong></TableCell>
                      <TableCell><strong>Reference #</strong></TableCell>
                      <TableCell><strong>Entered By</strong></TableCell>
                      <TableCell><strong>Notes</strong></TableCell>
                      <TableCell align="center"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paymentHistory.map((pmt) => {
                      const isRefund = pmt.isRefund;
                      return (
                        <TableRow
                          key={`${pmt.type}-${pmt.id}`}
                          sx={isRefund ? { bgcolor: '#fff5f5' } : {}}
                        >
                          <TableCell sx={isRefund ? { color: '#ef4444' } : {}}>
                            {formatDate(pmt.date)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={pmt.method}
                              size="small"
                              variant="outlined"
                              color={isRefund ? 'error' : pmt.type === 'online' ? 'primary' : 'default'}
                            />
                            {pmt.cardLastFour && (
                              <Typography variant="caption" sx={{ ml: 1 }}>
                                ****{pmt.cardLastFour}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 'bold', color: isRefund ? '#ef4444' : '#2e7d32' }}
                          >
                            {isRefund ? `-${formatCurrency(pmt.amount)}` : `+${formatCurrency(pmt.amount)}`}
                          </TableCell>
                          <TableCell>{pmt.referenceNumber || '-'}</TableCell>
                          <TableCell>{pmt.enteredBy || '-'}</TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }} title={pmt.notes}>
                              {pmt.notes || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {isRefund ? (
                              <Chip label="Refunded" size="small" color="error" sx={{ fontSize: '0.7rem', height: 20 }} />
                            ) : pmt.canRemove ? (
                              <Tooltip title="Remove Payment">
                                <IconButton
                                  size="small" color="error"
                                  onClick={() => setDeleteConfirm({ open: true, payment: pmt })}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" color="text.secondary">Online</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No payments recorded yet.
              </Typography>
            )}
          </Paper>

          {/* Audit Log */}
          <Paper sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 2, boxShadow: 'none' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AuditIcon sx={{ color: '#003063' }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#003063' }}>
                Audit Log
              </Typography>
            </Box>
            {auditLog && auditLog.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {auditLog.map((log) => {
                  let details = {};
                  try { details = JSON.parse(log.Details || '{}'); } catch { details = {}; }
                  const isRefundLog = log.Action?.toLowerCase().includes('refund');
                  return (
                    <Box
                      key={log.AuditLogID}
                      sx={{
                        p: 1.5, borderRadius: 1,
                        bgcolor: isRefundLog ? '#fff5f5' : '#f8f9fa',
                        borderLeft: `3px solid ${isRefundLog ? '#ef4444' : '#003063'}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.25 }}>
                        <Typography variant="body2" fontWeight="bold">{log.Action}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(log.CreatedAt)}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        by {log.PerformedByName || 'System'}
                        {details.method ? ` · ${details.method}` : ''}
                        {details.transactionId ? ` · Ref: ${details.transactionId}` : ''}
                        {details.items ? ` · Item: ${details.items}` : ''}
                      </Typography>
                      {details.amount !== undefined && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.25 }}>
                          Amount: {formatCurrency(details.amount)}
                        </Typography>
                      )}
                      {details.notes && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {details.notes}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No audit entries yet.
              </Typography>
            )}
          </Paper>
        </Box>
      </div>

      {/* Apply Payment Modal */}
      <ApplyPaymentModal
        open={paymentModal}
        order={order ? {
          ...order,
          CustomerName: customer?.name || '',
          CustomerFirstName: customer?.firstName || '',
          CustomerLastName: customer?.lastName || '',
          CustomerAddress: customer?.address || '',
          CustomerCity: customer?.city || '',
          CustomerState: customer?.state || '',
          CustomerZip: customer?.zip || '',
          AmountDue: summary.remainingBalance,
        } : null}
        onClose={handlePaymentModalClose}
        token={token}
      />

      {/* Issue Refund Modal */}
      <IssueRefundModal
        open={refundModal}
        onClose={handleRefundModalClose}
        order={order}
        orderItems={orderItems}
        customer={customer}
        token={token}
      />

      {/* Delete Payment Confirmation */}
      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, payment: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#d32f2f' }}>Remove Payment</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to remove this payment?
          </Typography>
          {deleteConfirm.payment && (
            <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
              <Typography variant="body2"><strong>Method:</strong> {deleteConfirm.payment.method}</Typography>
              <Typography variant="body2"><strong>Amount:</strong> {formatCurrency(deleteConfirm.payment.amount)}</Typography>
              <Typography variant="body2"><strong>Reference:</strong> {deleteConfirm.payment.referenceNumber || '-'}</Typography>
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            This will recalculate the order balance and update the payment status accordingly.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setDeleteConfirm({ open: false, payment: null })} variant="outlined" disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeletePayment}
            variant="contained" color="error" disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {isDeleting ? 'Removing...' : 'Remove Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </main>
  );
};

export default OrderDetail;
